import { useState, useEffect } from 'react';

interface NetworkStatusHook {
  isOnline: boolean;
  wasOffline: boolean;
  reconnecting: boolean;
  lastOfflineAt: Date | null;
  connectionCount: number;
}

/**
 * Hook to monitor and handle network connectivity status for the application
 * 
 * @returns NetworkStatusHook object containing network state info
 */
export const useNetworkStatus = (): NetworkStatusHook => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [reconnecting, setReconnecting] = useState<boolean>(false);
  const [lastOfflineAt, setLastOfflineAt] = useState<Date | null>(null);
  const [connectionCount, setConnectionCount] = useState<number>(0);

  useEffect(() => {
    const handleOnline = () => {
      if (!isOnline) {
        setReconnecting(true);
        // Simulate reconnection delay or logic if needed
        setTimeout(() => {
          setIsOnline(true);
          setWasOffline(true);
          setConnectionCount(prev => prev + 1);
          setReconnecting(false);
        }, 500);
      }
    };

    const handleOffline = () => {
      if (isOnline) {
        setIsOnline(false);
        setLastOfflineAt(new Date());
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Handle initial state
    if (navigator.onLine !== isOnline) {
      if (navigator.onLine) {
        handleOnline();
      } else {
        handleOffline();
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  return {
    isOnline,
    wasOffline,
    reconnecting,
    lastOfflineAt,
    connectionCount
  };
};

export default useNetworkStatus; 
